import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import openPdf from '../../api/openPdf';
import useGoogleDrive from '../../hooks/useGoogleDrive';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import AppointmentModal from '../../components/AppointmentModal';

const activityStatusLabels = {
  pending: { label: 'Pendiente', color: 'badge-warning' },
  in_progress: { label: 'En progreso', color: 'badge-info' },
  completed: { label: 'Completado', color: 'badge-success' },
};

const docStatusLabels = {
  pending: { label: 'Pendiente', color: 'badge-warning' },
  delivered: { label: 'Entregado', color: 'badge-success' },
  rejected: { label: 'Rechazado', color: 'badge-danger' },
  not_required: { label: 'No requerido', color: 'badge-light' },
};

const budgetStatusLabels = {
  draft:     { label: 'Borrador',  color: 'badge-secondary' },
  sent:      { label: 'Enviado',   color: 'badge-info' },
  accepted:  { label: 'Aceptado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const emptyActivityForm = { activity_type_id: '', title: '', description: '', status: 'pending', interviewed_with: '' };
const emptyDocForm = { name: '', description: '', required: true, status: 'pending', delivered_by: '', received_by_user_id: '', notes: '' };

const CaseFileDetail = () => {
  const { id } = useParams();
  const [caseFile, setCaseFile] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState(emptyActivityForm);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docForm, setDocForm] = useState(emptyDocForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', assigned_user_id: '', notes: '', procedure_type_id: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Presupuestos
  const [budgets, setBudgets] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);

  const fileInputRef = useRef(null);
  const currentDocRef = useRef(null);
  const { user } = useAuth();
  const { connected, connect } = useGoogleDrive();

  const fetchNotes = async () => {
    try {
      const res = await api.get(`/case-files/${id}/notes`);
      setNotes(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchChangeLogs = async () => {
    if (user?.role !== 'notary') return;
    setLoadingLogs(true);
    try {
      const res = await api.get(`/change-logs/case-file/${id}`);
      setChangeLogs(res.data);
    } catch (error) { console.error(error); }
    finally { setLoadingLogs(false); }
  };

  const fetchAuditLog = async () => {
    if (user?.role !== 'notary') return;
    setLoadingAudit(true);
    try {
      const res = await api.get(`/activity-log/case-files/${id}`);
      setAuditLog(res.data);
    } catch (error) { console.error(error); }
    finally { setLoadingAudit(false); }
  };

  const fetchCaseFile = async () => {
    try {
      const res = await api.get(`/case-files/${id}`);
      setCaseFile(res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchBudgets = async () => {
    setLoadingBudgets(true);
    try {
      const res = await api.get('/budgets', { params: { case_file_id: id, include_cancelled: 1 } });
      setBudgets(res.data.data || []);
    } catch (error) { console.error(error); }
    finally { setLoadingBudgets(false); }
  };

  useEffect(() => {
    Promise.all([
      fetchCaseFile(),
      api.get('/activity-types').then(r => setActivityTypes(r.data)),
      api.get('/users').then(r => setUsers(r.data)),
    ]);
    fetchChangeLogs();
    fetchAuditLog();
    fetchNotes();
    fetchBudgets();
  }, [id]);

  const handleStatusChange = async (status) => {
    await api.put(`/case-files/${id}`, { status });
    fetchCaseFile();
    toast.success('Estado actualizado');
  };

  const openEditModal = () => {
    setEditForm({
      title: caseFile.title || '',
      assigned_user_id: caseFile.assigned_user_id || '',
      notes: caseFile.notes || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await api.put(`/case-files/${id}`, editForm);
      setCaseFile(prev => ({ ...prev, ...res.data }));
      setShowEditModal(false);
      toast.success('Expediente actualizado');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateFolder = async () => {
    setCreatingFolder(true);
    try {
      await api.post(`/case-files/${id}/drive/folder`);
      fetchCaseFile();
      toast.success('Carpeta creada en Google Drive');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear carpeta');
    } finally { setCreatingFolder(false); }
  };

  const handleUploadClick = (doc) => {
    currentDocRef.current = doc.id;
    fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const docId = currentDocRef.current;
    setUploadingDoc(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/case-files/${id}/documents/${docId}/drive/upload`, formData);
      fetchCaseFile();
      toast.success('Archivo subido a Google Drive');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al subir archivo');
    } finally { setUploadingDoc(null); e.target.value = ''; }
  };

  const handleDeleteDriveFile = async (docId, driveFileId) => {
    if (!window.confirm('¿Eliminar este archivo de Drive?')) return;
    try {
      await api.delete(`/case-files/${id}/documents/${docId}/drive/files/${driveFileId}`);
      fetchCaseFile();
      toast.success('Archivo eliminado');
    } catch (error) { toast.error('Error al eliminar archivo'); }
  };

  const handleNewActivity = () => {
    setEditingActivity(null);
    setActivityForm(emptyActivityForm);
    setShowActivityForm(true);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setActivityForm({
      activity_type_id: activity.activity_type_id || '',
      title: activity.title,
      description: activity.description || '',
      status: activity.status,
      interviewed_with: activity.interviewed_with || '',
    });
    setShowActivityForm(true);
  };

  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingActivity) {
        await api.put(`/case-files/${id}/activities/${editingActivity.id}`, activityForm);
        toast.success('Actividad actualizada');
      } else {
        await api.post(`/case-files/${id}/activities`, activityForm);
        toast.success('Actividad registrada');
      }
      setShowActivityForm(false);
      setEditingActivity(null);
      setActivityForm(emptyActivityForm);
      fetchCaseFile();
    } catch (error) { toast.error('Error al guardar actividad'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    await api.delete(`/case-files/${id}/activities/${activityId}`);
    fetchCaseFile();
    toast.success('Actividad eliminada');
  };

  const handleUpdateDoc = async (docId, data) => {
    try {
      await api.put(`/case-files/${id}/documents/${docId}`, data);
      fetchCaseFile();
      toast.success('Documento actualizado');
    } catch (error) { toast.error('Error al actualizar documento'); }
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setDocForm({
      name: doc.name, description: doc.description || '', status: doc.status,
      delivered_by: doc.delivered_by || '', received_by_user_id: doc.received_by_user_id || '', notes: doc.notes || '',
    });
    setShowDocForm(true);
  };

  const handleSubmitDoc = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingDoc) {
        await api.put(`/case-files/${id}/documents/${editingDoc.id}`, docForm);
        toast.success('Documento actualizado');
      } else {
        await api.post(`/case-files/${id}/documents`, docForm);
        toast.success('Documento agregado');
      }
      setShowDocForm(false);
      setEditingDoc(null);
      setDocForm(emptyDocForm);
      fetchCaseFile();
    } catch (error) { toast.error('Error al guardar documento'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    await api.delete(`/case-files/${id}/documents/${docId}`);
    fetchCaseFile();
    toast.success('Documento eliminado');
  };

  const fmtMoney = (budget) => {
    const total = budget.totals?.grand_total ?? 0;
    if (budget.currency === 'usd') {
      return `$${(total / (Number(budget.usd_exchange_rate) || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    }
    return `$${Number(total).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!caseFile) return <div className="alert alert-danger">Expediente no encontrado</div>;

  const deliveredDocs = caseFile.documents?.filter(d => d.status === 'delivered').length || 0;
  const totalDocs = caseFile.documents?.length || 0;
  const activeBudgets = budgets.filter(b => b.status !== 'cancelled').length;

  return (
    <div className="container-fluid">
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelected} />

      <Breadcrumb items={[
        { label: 'Expedientes', to: '/case-files' },
        { label: caseFile.client?.name, to: `/clients/${caseFile.client_id}` },
        { label: caseFile.title },
      ]} />

      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-start mb-3">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 className="mb-0" style={{ wordBreak: 'break-word' }}>{caseFile.title}</h4>
          <small className="text-muted">
            <Link to={`/clients/${caseFile.client_id}`}>{caseFile.client?.name}</Link>
            {' · '}{caseFile.procedure_type?.name}
          </small>
        </div>
        <div className="d-flex" style={{ gap: '8px', flexShrink: 0 }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={openEditModal}>
            <i className="ti-pencil mr-1"></i> Editar
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowAppointmentModal(true)}
          >
            📅 Agendar cita
          </button>
          <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => openPdf(`/api/case-files/${id}/pdf`)}
            >
              <i className="ti-printer mr-1"></i> PDF
          </button>
          <Link to="/case-files" className="btn btn-outline-secondary btn-sm">
            <i className="ti-arrow-left mr-1"></i> Regresar
          </Link>
        </div>

      </div>

      {/* Toggle móvil */}
      <div className="d-md-none mb-3">
        <button className="btn btn-outline-secondary btn-sm btn-block"
          onClick={() => setShowInfoPanel(!showInfoPanel)}>
          <i className={`ti-${showInfoPanel ? 'angle-up' : 'angle-down'} mr-1`}></i>
          {showInfoPanel ? 'Ocultar información' : 'Ver información del expediente'}
        </button>
      </div>

      <div className="row">
        {/* Panel lateral */}
        <div className={`col-12 col-md-3 ${showInfoPanel ? '' : 'd-none d-md-block'}`}>
          <div className="card mb-4">
            <div className="card-header"><h6 className="mb-0">Información</h6></div>
            <div className="card-body">
              <div className="form-group">
                <label className="small text-muted">Estado</label>
                <select className="form-control form-control-sm" value={caseFile.status}
                  onChange={(e) => handleStatusChange(e.target.value)}>
                  <option value="open">Abierto</option>
                  <option value="in_progress">En proceso</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr><td className="text-muted small">Responsable</td><td className="small">{caseFile.assigned_user?.name}</td></tr>
                  <tr>
                    <td className="text-muted small">Tiempo</td>
                    <td><span className={`badge ${caseFile.status === 'completed' ? 'badge-light' : 'badge-danger'}`}>{caseFile.elapsed_time}</span></td>
                  </tr>
                  <tr>
                    <td className="text-muted small">Apertura</td>
                    <td className="small">{new Date(caseFile.opened_at || caseFile.created_at).toLocaleDateString('es-MX')}</td>
                  </tr>
                  <tr>
                    <td className="text-muted small">Documentos</td>
                    <td><span className={`badge ${deliveredDocs === totalDocs && totalDocs > 0 ? 'badge-success' : 'badge-warning'}`}>{deliveredDocs}/{totalDocs}</span></td>
                  </tr>
                  <tr><td className="text-muted small">Actividades</td><td className="small">{caseFile.activities?.length || 0}</td></tr>
                  <tr>
                    <td className="text-muted small">Presupuestos</td>
                    <td><span className="badge badge-secondary">{activeBudgets}</span></td>
                  </tr>
                </tbody>
              </table>
              {caseFile.notes && (<><hr /><p className="text-muted small mb-1">Notas</p><p className="small mb-0">{caseFile.notes}</p></>)}
              <hr />
              <p className="text-muted small mb-2 font-weight-bold">Google Drive</p>
              {!connected ? (
                <button className="btn btn-sm btn-outline-secondary btn-block" onClick={connect}>
                  <i className="ti-google mr-1"></i> Conectar Drive
                </button>
              ) : caseFile.google_drive_folder_url ? (
                <a href={caseFile.google_drive_folder_url} target="_blank" rel="noreferrer"
                  className="btn btn-sm btn-outline-success btn-block">
                  <i className="ti-folder mr-1"></i> Ver carpeta en Drive
                </a>
              ) : (
                <button className="btn btn-sm btn-success btn-block" onClick={handleCreateFolder} disabled={creatingFolder}>
                  {creatingFolder ? 'Creando...' : <><i className="ti-folder mr-1"></i> Crear carpeta en Drive</>}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="col-12 col-md-9">
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <ul className="nav nav-tabs mb-0 flex-nowrap">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documents')}>
                  Documentos <span className="badge badge-secondary ml-1">{totalDocs}</span>
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'activities' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activities')}>
                  Actividades <span className="badge badge-secondary ml-1">{caseFile.activities?.length || 0}</span>
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'budgets' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('budgets'); fetchBudgets(); }}>
                  Presupuestos <span className="badge badge-secondary ml-1">{activeBudgets}</span>
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'notes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('notes')}>
                  Notas <span className="badge badge-secondary ml-1">{notes.length}</span>
                </button>
              </li>
              {user?.role === 'notary' && (
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('logs')}>
                    Historial <span className="badge badge-secondary ml-1">{changeLogs.length}</span>
                  </button>
                </li>
              )}
              {user?.role === 'notary' && (
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'audit' ? 'active' : ''}`}
                  onClick={() => setActiveTab('audit')}>
                  Auditoría <span className="badge badge-secondary ml-1">{auditLog.length}</span>
                </button>
              </li>
              )}
            </ul>
          </div>

          <div className="card" style={{ borderTopLeftRadius: 0 }}>

            {/* ── TAB DOCUMENTOS ── */}
            {activeTab === 'documents' && (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Checklist de Documentos</h6>
                  <button className="btn btn-primary btn-sm"
                    onClick={() => { setEditingDoc(null); setDocForm(emptyDocForm); setShowDocForm(true); }}>
                    <i className="ti-plus mr-1"></i> Agregar
                  </button>
                </div>
                <div className="card-body">
                  {showDocForm && (
                    <div className="border rounded p-3 mb-4 bg-light">
                      <h6 className="mb-3">{editingDoc ? 'Editar Documento' : 'Nuevo Documento'}</h6>
                      <form onSubmit={handleSubmitDoc}>
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Nombre <span className="text-danger">*</span></label>
                              <input type="text" className="form-control" value={docForm.name || ''}
                                onChange={(e) => setDocForm({ ...docForm, name: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Estado</label>
                              <select className="form-control" value={docForm.status || 'pending'}
                                onChange={(e) => setDocForm({ ...docForm, status: e.target.value })}>
                                <option value="pending">Pendiente</option>
                                <option value="delivered">Entregado</option>
                                <option value="rejected">Rechazado</option>
                                <option value="not_required">No requerido</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Entregado por</label>
                              <input type="text" className="form-control" value={docForm.delivered_by || ''}
                                onChange={(e) => setDocForm({ ...docForm, delivered_by: e.target.value })} />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Recibido por</label>
                              <select className="form-control" value={docForm.received_by_user_id || ''}
                                onChange={(e) => setDocForm({ ...docForm, received_by_user_id: e.target.value })}>
                                <option value="">Seleccionar...</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-group">
                              <label>Descripción / Notas</label>
                              <textarea className="form-control" rows="2" value={docForm.description || ''}
                                onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}></textarea>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                            {submitting ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => { setShowDocForm(false); setEditingDoc(null); }}>Cancelar</button>
                        </div>
                      </form>
                    </div>
                  )}

                  {caseFile.documents?.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay documentos en el checklist</div>
                  ) : (
                    caseFile.documents?.map((doc) => (
                      <div key={doc.id} className="border rounded mb-3">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: '4px' }}>
                                <span className={`badge ${docStatusLabels[doc.status]?.color}`}>
                                  {docStatusLabels[doc.status]?.label}
                                </span>
                                <strong style={{ wordBreak: 'break-word' }}>{doc.name}</strong>
                                {doc.required && <span className="badge badge-danger" style={{ fontSize: '9px' }}>Requerido</span>}
                              </div>
                              {doc.delivered_by && (
                                <small className="text-muted d-block mt-1">
                                  Entregado por: {doc.delivered_by}
                                  {doc.received_by_user && ` · Recibido: ${doc.received_by_user.name}`}
                                  {doc.delivered_at && ` · ${new Date(doc.delivered_at).toLocaleDateString('es-MX')}`}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="d-flex flex-wrap align-items-center" style={{ gap: '6px' }}>
                            <select className="form-control form-control-sm" style={{ width: '140px' }}
                              value={doc.status}
                              onChange={(e) => handleUpdateDoc(doc.id, { status: e.target.value })}>
                              <option value="pending">Pendiente</option>
                              <option value="delivered">Entregado</option>
                              <option value="rejected">Rechazado</option>
                              <option value="not_required">No requerido</option>
                            </select>
                            {connected && caseFile.google_drive_folder_id && (
                              <button className="btn btn-sm btn-outline-success"
                                onClick={() => handleUploadClick(doc)}
                                disabled={uploadingDoc === doc.id} title="Subir archivo a Drive">
                                {uploadingDoc === doc.id
                                  ? <span className="spinner-border spinner-border-sm"></span>
                                  : <i className="ti-upload"></i>}
                              </button>
                            )}
                            {doc.drive_files?.length > 0 && (
                              <button className="btn btn-sm btn-outline-info"
                                onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}>
                                <i className="ti-files"></i> {doc.drive_files.length}
                              </button>
                            )}
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditDoc(doc)}>
                              <i className="ti-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteDoc(doc.id)}>
                              <i className="ti-trash"></i>
                            </button>
                          </div>
                        </div>
                        {expandedDoc === doc.id && doc.drive_files?.length > 0 && (
                          <div className="border-top px-3 py-2 bg-light">
                            <small className="text-muted font-weight-bold">ARCHIVOS EN DRIVE</small>
                            {doc.drive_files.map((file) => (
                              <div key={file.id} className="d-flex justify-content-between align-items-center py-1 flex-wrap" style={{ gap: '6px' }}>
                                <div>
                                  <a href={file.google_drive_file_url} target="_blank" rel="noreferrer" className="text-primary">
                                    <i className="ti-file mr-1"></i>{file.name}
                                  </a>
                                  <small className="text-muted ml-2">
                                    {file.uploaded_by?.name} · {new Date(file.created_at).toLocaleDateString('es-MX')}
                                  </small>
                                </div>
                                <button className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteDriveFile(doc.id, file.id)}>
                                  <i className="ti-trash"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* ── TAB ACTIVIDADES ── */}
            {activeTab === 'activities' && (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Actividades</h6>
                  <button className="btn btn-primary btn-sm" onClick={handleNewActivity}>
                    <i className="ti-plus mr-1"></i> Nueva
                  </button>
                </div>
                <div className="card-body">
                  {showActivityForm && (
                    <div className="border rounded p-3 mb-4 bg-light">
                      <h6 className="mb-3">{editingActivity ? 'Editar Actividad' : 'Nueva Actividad'}</h6>
                      <form onSubmit={handleSubmitActivity}>
                        <div className="row">
                          <div className="col-12 col-md-4">
                            <div className="form-group">
                              <label>Tipo <span className="text-danger">*</span></label>
                              <select className="form-control" value={activityForm.activity_type_id}
                                onChange={(e) => setActivityForm({ ...activityForm, activity_type_id: e.target.value })} required>
                                <option value="">Seleccionar...</option>
                                {activityTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form-group">
                              <label>Estado</label>
                              <select className="form-control" value={activityForm.status}
                                onChange={(e) => setActivityForm({ ...activityForm, status: e.target.value })}>
                                <option value="pending">Pendiente</option>
                                <option value="in_progress">En progreso</option>
                                <option value="completed">Completado</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form-group">
                              <label>Se entrevistó con</label>
                              <input type="text" className="form-control" value={activityForm.interviewed_with}
                                onChange={(e) => setActivityForm({ ...activityForm, interviewed_with: e.target.value })} />
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-group">
                              <label>Título <span className="text-danger">*</span></label>
                              <input type="text" className="form-control" value={activityForm.title}
                                onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-group">
                              <label>Descripción</label>
                              <textarea className="form-control" rows="2" value={activityForm.description}
                                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}></textarea>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                            {submitting ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => { setShowActivityForm(false); setEditingActivity(null); }}>Cancelar</button>
                        </div>
                      </form>
                    </div>
                  )}

                  {caseFile.activities?.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay actividades registradas</div>
                  ) : (
                    caseFile.activities?.map((activity) => (
                      <div key={activity.id} className="border rounded p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: '6px' }}>
                          <div>
                            <span className={`badge ${activity.activity_type?.color || 'badge-secondary'} mr-2`}>
                              {activity.activity_type?.name || 'Sin tipo'}
                            </span>
                            <strong>{activity.title}</strong>
                          </div>
                          <span className={`badge ${activityStatusLabels[activity.status]?.color}`}>
                            {activityStatusLabels[activity.status]?.label}
                          </span>
                        </div>
                        {activity.description && <p className="mt-2 mb-1 text-muted small">{activity.description}</p>}
                        {activity.interviewed_with && (
                          <p className="mb-1 small"><i className="ti-user mr-1"></i> Se entrevistó con: <strong>{activity.interviewed_with}</strong></p>
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap" style={{ gap: '6px' }}>
                          <small className="text-muted">
                            <i className="ti-user mr-1"></i>{activity.user?.name} &nbsp;|&nbsp;
                            <i className="ti-time mr-1"></i>{activity.elapsed_time} &nbsp;|&nbsp;
                            {new Date(activity.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </small>
                          <div className="d-flex" style={{ gap: '6px' }}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditActivity(activity)}>
                              <i className="ti-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteActivity(activity.id)}>
                              <i className="ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* ── TAB PRESUPUESTOS ── */}
            {activeTab === 'budgets' && (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Presupuestos</h6>
                  <Link to={`/budgets/create?case_file_id=${id}`} className="btn btn-primary btn-sm">
                    <i className="ti-plus mr-1"></i> Nuevo Presupuesto
                  </Link>
                </div>
                <div className="card-body p-0">
                  {loadingBudgets ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                  ) : budgets.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti-receipt" style={{ fontSize: '2.5rem' }}></i>
                      <p className="mt-2 mb-3">No hay presupuestos para este expediente</p>
                      <Link to={`/budgets/create?case_file_id=${id}`} className="btn btn-primary btn-sm">
                        <i className="ti-plus mr-1"></i> Crear primer presupuesto
                      </Link>
                    </div>
                  ) : (
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Folio</th>
                          <th>Tipo</th>
                          <th>Total</th>
                          <th className="mobile-hide">Pagado</th>
                          <th>Estado</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {budgets.map(budget => {
                          const grandTotal = budget.totals?.grand_total ?? 0;
                          const totalPaid  = budget.totals?.total_paid  ?? 0;
                          const balance    = grandTotal - totalPaid;
                          return (
                            <tr key={budget.id} style={{ opacity: budget.status === 'cancelled' ? 0.5 : 1 }}>
                              <td>
                                <span className="font-weight-bold small">{budget.folio || `#${budget.id}`}</span>
                                <div className="small text-muted">{new Date(budget.created_at).toLocaleDateString('es-MX')}</div>
                              </td>
                              <td><small>{budget.document_type?.name}</small></td>
                              <td>
                                <span className="small font-weight-bold">{fmtMoney(budget)}</span>
                                {balance > 0 && grandTotal > 0 && (
                                  <div className="small text-danger">
                                    Saldo: ${Number(balance).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                  </div>
                                )}
                              </td>
                              <td className="mobile-hide">
                                {totalPaid > 0
                                  ? <span className="badge badge-success">${Number(totalPaid).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                  : <span className="text-muted small">—</span>}
                              </td>
                              <td>
                                <span className={`badge ${budgetStatusLabels[budget.status]?.color}`}>
                                  {budgetStatusLabels[budget.status]?.label}
                                </span>
                              </td>
                              <td>
                                <Link to={`/budgets/${budget.id}`} className="btn btn-sm btn-outline-primary">
                                  <i className="ti-eye"></i>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ── TAB NOTAS ── */}
            {activeTab === 'notes' && (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Notas Internas</h6>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <textarea className="form-control mb-2" rows="3" placeholder="Escribe una nota rápida..."
                      value={newNote} onChange={(e) => setNewNote(e.target.value)}></textarea>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '8px' }}>
                      {(user?.role === 'notary' || user?.role === 'supervisor') && (
                        <div className="custom-control custom-checkbox">
                          <input type="checkbox" className="custom-control-input" id="isPrivate"
                            checked={isPrivateNote} onChange={(e) => setIsPrivateNote(e.target.checked)} />
                          <label className="custom-control-label" htmlFor="isPrivate">
                            <span className="text-muted small">🔒 Nota privada</span>
                          </label>
                        </div>
                      )}
                      <button className="btn btn-primary btn-sm ml-auto"
                        disabled={!newNote.trim() || savingNote}
                        onClick={async () => {
                          if (!newNote.trim()) return;
                          setSavingNote(true);
                          try {
                            await api.post(`/case-files/${id}/notes`, { content: newNote, is_private: isPrivateNote });
                            setNewNote(''); setIsPrivateNote(false);
                            fetchNotes(); toast.success('Nota agregada');
                          } catch { toast.error('Error al guardar nota'); }
                          finally { setSavingNote(false); }
                        }}>
                        {savingNote ? 'Guardando...' : 'Agregar nota'}
                      </button>
                    </div>
                  </div>

                  {notes.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay notas registradas</div>
                  ) : (
                    notes.map(note => (
                      <div key={note.id} className="border rounded p-3 mb-3"
                        style={{ backgroundColor: note.is_private ? '#FFFBEB' : '#fff', borderColor: note.is_private ? '#FCD34D' : '#e9ecef' }}>
                        {editingNote === note.id ? (
                          <div>
                            <textarea className="form-control mb-2" rows="3" value={editNoteContent}
                              onChange={(e) => setEditNoteContent(e.target.value)}></textarea>
                            <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                              <button className="btn btn-sm btn-primary"
                                onClick={async () => {
                                  setSavingNote(true);
                                  try {
                                    await api.put(`/case-files/${id}/notes/${note.id}`, { content: editNoteContent, is_private: note.is_private });
                                    setEditingNote(null); fetchNotes(); toast.success('Nota actualizada');
                                  } catch { toast.error('Error al actualizar'); }
                                  finally { setSavingNote(false); }
                                }} disabled={savingNote}>Guardar</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingNote(null)}>Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap" style={{ gap: '6px' }}>
                              <div className="d-flex align-items-center">
                                <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#5066E1', fontSize: 12, flexShrink: 0, marginRight: 8 }}>
                                  {note.user?.name?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <small className="font-weight-bold">{note.user?.name}</small>
                                  <small className="text-muted ml-2">
                                    {new Date(note.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </small>
                                  {note.is_private && <span className="badge badge-warning ml-2" style={{ fontSize: '10px' }}>🔒 Privada</span>}
                                </div>
                              </div>
                              <div className="d-flex" style={{ gap: '6px' }}>
                                {note.user_id === user?.id && (
                                  <button className="btn btn-sm btn-outline-secondary"
                                    onClick={() => { setEditingNote(note.id); setEditNoteContent(note.content); }}>
                                    <i className="ti-pencil"></i>
                                  </button>
                                )}
                                {(note.user_id === user?.id || user?.role === 'notary') && (
                                  <button className="btn btn-sm btn-outline-danger"
                                    onClick={async () => {
                                      if (!window.confirm('¿Eliminar esta nota?')) return;
                                      try { await api.delete(`/case-files/${id}/notes/${note.id}`); fetchNotes(); toast.success('Nota eliminada'); }
                                      catch { toast.error('Error al eliminar'); }
                                    }}>
                                    <i className="ti-trash"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="mb-0" style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{note.content}</p>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* ── TAB HISTORIAL ── */}
            {activeTab === 'logs' && user?.role === 'notary' && (
              <>
                <div className="card-header"><h6 className="mb-0">Historial de Cambios</h6></div>
                <div className="card-body p-0 mobile-card-list" style={{ overflowX: 'auto' }}>
                  {loadingLogs ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                  ) : changeLogs.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay cambios registrados</div>
                  ) : (
                   <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '35%' }}>Descripción</th>
                        <th className="mobile-hide" style={{ width: '18%' }}>Anterior</th>
                        <th className="mobile-hide" style={{ width: '18%' }}>Nuevo</th>
                        <th className="mobile-hide" style={{ width: '15%' }}>Por</th>
                        <th style={{ width: '14%' }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogs.map(log => (
                        <tr key={log.id}>
                          <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            <small>{log.description}</small>
                            <div className="d-md-none mt-1"><small className="text-muted">{log.user?.name}</small></div>
                          </td>
                          <td className="mobile-hide">
                            {log.old_value
                              ? <span className="badge badge-light text-muted"
                                  style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                                  title={log.old_value}>
                                  {log.old_value}
                                </span>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="mobile-hide">
                            {log.new_value
                              ? (log.field === 'drive_folder' || log.field === 'file_uploaded'
                                  ? <a href={log.new_value} target="_blank" rel="noreferrer"
                                      className="badge badge-success">Ver en Drive</a>
                                  : <span className="badge badge-primary"
                                      style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                                      title={log.new_value}>
                                      {log.new_value}
                                    </span>)
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="mobile-hide"><small>{log.user?.name}</small></td>
                          <td>
                            <small className="text-muted">
                              {new Date(log.created_at).toLocaleDateString('es-MX', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                </div>
              </>
            )}

            {/* ── TAB AUDITORÍA ── */}
            {activeTab === 'audit' && user?.role === 'notary' && (
              <>
                <div className="card-header"><h6 className="mb-0">Log de Auditoría</h6></div>
                <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                  {loadingAudit ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                  ) : auditLog.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay registros de auditoría</div>
                  ) : (
                    <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '12%' }}>Acción</th>
                          <th style={{ width: '18%' }}>Por</th>
                          <th style={{ width: '35%' }}>Cambios</th>
                          <th style={{ width: '18%' }}>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.map(log => (
                          <tr key={log.id}>
                            <td>
                              <span className={`badge ${log.event === 'created' ? 'badge-success' : log.event === 'deleted' ? 'badge-danger' : 'badge-info'}`}>
                                {log.event === 'created' ? 'Creado' : log.event === 'deleted' ? 'Eliminado' : 'Editado'}
                              </span>
                            </td>
                            <td><small>{log.causer}</small></td>
                            <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                              {log.old && log.new ? (
                                <div style={{ fontSize: '11px' }}>
                                  {Object.keys(log.new).map(key => (
                                    log.old[key] !== log.new[key] ? (
                                      <div key={key} className="mb-1">
                                        <span className="text-muted">{key}:</span>{' '}
                                        <span className="text-danger" style={{ textDecoration: 'line-through' }}>{String(log.old[key] ?? '—')}</span>
                                        {' → '}
                                        <span className="text-success">{String(log.new[key] ?? '—')}</span>
                                      </div>
                                    ) : null
                                  ))}
                                </div>
                              ) : (
                                <small className="text-muted">{log.description}</small>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(log.created_at).toLocaleDateString('es-MX', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
      {showAppointmentModal && (
        <AppointmentModal
          prefillClientId={caseFile.client_id}
          prefillCaseFileId={caseFile.id}
          onClose={() => setShowAppointmentModal(false)}
          onSaved={() => setShowAppointmentModal(false)}
        />
      )}
      {/* Modal editar expediente */}
      {showEditModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar expediente</h5>
                <button className="close" onClick={() => setShowEditModal(false)}><span>×</span></button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="small font-weight-bold">Título</label>
                  <input
                    className="form-control"
                    value={editForm.title}
                    onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Responsable</label>
                  <select
                    className="form-control"
                    value={editForm.assigned_user_id}
                    onChange={e => setEditForm({ ...editForm, assigned_user_id: e.target.value })}
                  >
                    <option value="">Sin asignar</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Notas internas</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editForm.notes}
                    onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                  {savingEdit ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseFileDetail;